import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Star } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion'
import {
  getFeatureDefinitions,
  deleteFeatureDefinition,
  groupFeatureDefinitions,
  type FeatureDefinition,
  type FeatureGroup,
} from '@/lib/api'
import { FeatureEditDrawer } from './FeatureEditDrawer'

interface FeatureDefinitionsPanelProps {
  serviceId: string
}

export function FeatureDefinitionsPanel({ serviceId }: FeatureDefinitionsPanelProps) {
  const queryClient = useQueryClient()
  const [selectedFeature, setSelectedFeature] = React.useState<FeatureDefinition | null>(null)
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const [isCreateMode, setIsCreateMode] = React.useState(false)
  const [prefilledGroup, setPrefilledGroup] = React.useState<{ code: string; label: string } | null>(null)

  // Load feature definitions
  const { data: features = [], isLoading } = useQuery({
    queryKey: ['feature-definitions', serviceId],
    queryFn: () => getFeatureDefinitions(serviceId),
  })

  // Group features
  const groups: FeatureGroup[] = React.useMemo(() => {
    return groupFeatureDefinitions(features)
  }, [features])

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (featureId: string) => deleteFeatureDefinition(serviceId, featureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-definitions', serviceId] })
      queryClient.invalidateQueries({ queryKey: ['service', serviceId] })
    },
  })

  const handleEdit = (feature: FeatureDefinition) => {
    setSelectedFeature(feature)
    setIsCreateMode(false)
    setPrefilledGroup(null)
    setDrawerOpen(true)
  }

  const handleCreate = (group?: { code: string; label: string }) => {
    setSelectedFeature(null)
    setIsCreateMode(true)
    setPrefilledGroup(group || null)
    setDrawerOpen(true)
  }

  const handleDelete = (feature: FeatureDefinition) => {
    if (confirm(`Delete feature "${feature.code}"? This will also remove it from all plans.`)) {
      deleteMutation.mutate(feature.id)
    }
  }

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">Loading features...</div>
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Feature Definitions</h3>
          <p className="text-sm text-muted-foreground">
            Define features that can be enabled/disabled per plan
          </p>
        </div>
        <Button onClick={() => handleCreate()} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Feature
        </Button>
      </div>

      {/* Groups Accordion */}
      {groups.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground">No features defined yet.</p>
          <Button
            variant="outline"
            onClick={() => handleCreate()}
            className="mt-4 gap-2"
          >
            <Plus className="h-4 w-4" />
            Add First Feature
          </Button>
        </div>
      ) : (
        <Accordion type="multiple" className="rounded-lg border" defaultValue={groups.map(g => g.group_code)}>
          {groups.map((group) => (
            <AccordionItem key={group.group_code} value={group.group_code}>
              <AccordionTrigger className="px-4 hover:no-underline">
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="font-mono">
                    {group.group_code}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {group.group_label_key}
                  </span>
                  <Badge variant="secondary" className="ml-2">
                    {group.features.length} features
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4">
                {/* Feature table */}
                <div className="rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-3 py-2 text-left font-medium">Code</th>
                        <th className="px-3 py-2 text-left font-medium">Label Key</th>
                        <th className="px-3 py-2 text-center font-medium w-20">Highlight</th>
                        <th className="px-3 py-2 text-center font-medium w-16">Order</th>
                        <th className="px-3 py-2 w-24"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.features.map((feature) => (
                        <tr key={feature.id} className="border-b last:border-b-0">
                          <td className="px-3 py-2">
                            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                              {feature.code}
                            </code>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {feature.feature_label_key}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {feature.is_highlight && (
                              <Star className="inline h-4 w-4 text-yellow-500 fill-yellow-500" />
                            )}
                          </td>
                          <td className="px-3 py-2 text-center text-muted-foreground">
                            {feature.sort_order}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex justify-end gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleEdit(feature)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                onClick={() => handleDelete(feature)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Add to group button */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCreate({ code: group.group_code, label: group.group_label_key })}
                  className="mt-2 gap-2"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add to {group.group_code}
                </Button>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Edit/Create Drawer */}
      <FeatureEditDrawer
        serviceId={serviceId}
        feature={selectedFeature}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        isCreateMode={isCreateMode}
        prefilledGroup={prefilledGroup}
        existingGroups={groups.map(g => ({ code: g.group_code, label: g.group_label_key }))}
      />
    </div>
  )
}
